import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import { 
  GraduationCap, 
  BookOpen, 
  Users, 
  Award, 
  CheckCircle, 
  ArrowRight,
  Clock,
  Globe,
  Target,
  BarChart3,
  Shield,
  Zap,
  MessageSquare,
  Video,
  Headphones,
  Calendar,
  Monitor,
  Smartphone,
  FileText,
  Star,
  Play,
  UserCheck,
  Briefcase,
  Code,
  Cloud
} from 'lucide-react';

const Training: React.FC = () => {
  const [activeFormat, setActiveFormat] = useState('all');

  const scrollToTrainingPrograms = () => {
    const element = document.getElementById('training-programs');
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const trainingPrograms = [
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Dynamics 365 Fundamentals",
      description: "Comprehensive training on Dynamics 365 Sales, Customer Service, and core functionalities.",
      modules: ["User interface navigation", "Lead and opportunity management", "Customer service cases", "Reporting and dashboards"],
      duration: "3 days",
      level: "Beginner",
      format: ["live", "online", "onsite"],
      category: "dynamics365"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Power Platform Development",
      description: "Learn to build custom applications and automate processes with Power Platform tools.",
      modules: ["Power Apps canvas apps", "Power Automate flows", "Power BI dashboards", "Power Virtual Agents"],
      duration: "5 days",
      level: "Intermediate",
      format: ["live", "online"],
      category: "powerplatform"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Microsoft 365 Administration",
      description: "Master Microsoft 365 administration, security, and compliance management.",
      modules: ["User management", "Security policies", "Compliance center", "Teams administration"],
      duration: "4 days",
      level: "Intermediate",
      format: ["live", "online", "onsite"],
      category: "microsoft365"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Power BI Analytics",
      description: "Advanced training on data visualization, modeling, and business intelligence with Power BI.",
      modules: ["Data modeling", "DAX formulas", "Advanced visualizations", "Report publishing"],
      duration: "3 days",
      level: "Intermediate",
      format: ["live", "online"],
      category: "powerplatform"
    },
    {
      icon: <Code className="w-6 h-6" />,
      title: "Dynamics 365 Customization",
      description: "Learn to customize and extend Dynamics 365 with plugins, workflows, and custom entities.",
      modules: ["Custom entities", "Business process flows", "Plugin development", "Web resources"],
      duration: "5 days",
      level: "Advanced",
      format: ["live", "onsite"],
      category: "dynamics365"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Change Management",
      description: "Training on leading digital transformation and managing organizational change.",
      modules: ["Change strategy", "Stakeholder engagement", "Communication planning", "Adoption metrics"],
      duration: "2 days",
      level: "Beginner",
      format: ["live", "online", "onsite"],
      category: "business"
    }
  ];

  const trainingFormats = [
    {
      id: "live",
      icon: <Video className="w-6 h-6" />,
      title: "Live Virtual Training",
      description: "Interactive live sessions with real-time Q&A and hands-on exercises.",
      features: ["Real-time interaction", "Live demonstrations", "Q&A sessions", "Breakout rooms"]
    },
    {
      id: "online",
      icon: <Monitor className="w-6 h-6" />,
      title: "Self-Paced Online",
      description: "Learn at your own pace with recorded sessions and interactive modules.",
      features: ["24/7 access", "Progress tracking", "Downloadable resources", "Certificate of completion"]
    },
    {
      id: "onsite",
      icon: <Users className="w-6 h-6" />,
      title: "On-Site Training",
      description: "Customized training delivered at your location with hands-on practice.",
      features: ["Customized content", "Team building", "Real data scenarios", "Immediate application"]
    }
  ];

  const certifications = [
    {
      title: "Microsoft Certified: Dynamics 365 Fundamentals",
      description: "Validate your foundational knowledge of Dynamics 365 applications.",
      icon: <Award className="w-8 h-8" />
    },
    {
      title: "Microsoft Certified: Power Platform Fundamentals",
      description: "Demonstrate your understanding of Power Platform capabilities.",
      icon: <Zap className="w-8 h-8" />
    },
    {
      title: "Microsoft Certified: Azure Fundamentals",
      description: "Prove your knowledge of cloud concepts and Azure services.",
      icon: <Cloud className="w-8 h-8" />
    },
    {
      title: "Microsoft 365 Certified: Administrator Expert",
      description: "Advanced certification for Microsoft 365 administration and security.",
      icon: <Shield className="w-8 h-8" />
    }
  ];

  const benefits = [
    "Expert-led training by Microsoft certified professionals",
    "Hands-on labs with real-world scenarios",
    "Official Microsoft training materials and resources",
    "Certificate of completion for all programs",
    "Flexible scheduling to fit your business needs",
    "Post-training support and consultation"
  ];

  const learningPaths = [
    {
      step: "01",
      title: "Skills Assessment",
      description: "Evaluate current skill levels and identify training needs for your team.",
      color: "bg-blue-500"
    },
    {
      step: "02",
      title: "Custom Curriculum",
      description: "Design personalized training curriculum based on your business requirements.",
      color: "bg-green-500"
    },
    {
      step: "03",
      title: "Interactive Learning",
      description: "Engage in hands-on training with real-world scenarios and practical exercises.",
      color: "bg-purple-500"
    },
    {
      step: "04",
      title: "Practical Application",
      description: "Apply learned concepts to your actual business processes and systems.",
      color: "bg-orange-500"
    },
    {
      step: "05",
      title: "Certification Prep",
      description: "Prepare for Microsoft certifications with practice exams and study materials.",
      color: "bg-red-500"
    },
    {
      step: "06",
      title: "Ongoing Support",
      description: "Receive continued support and advanced training as your needs evolve.",
      color: "bg-indigo-500"
    }
  ];

  const filteredPrograms = activeFormat === 'all' 
    ? trainingPrograms 
    : trainingPrograms.filter(program => program.format.includes(activeFormat));

  return (
    <>
      <SEOHead
        title="Professional Training | CRMONCE - Microsoft Technology Training Programs"
        description="Expert-led Microsoft Dynamics 365, Power Platform, and Azure training programs. Live virtual, online, and on-site training options. Get certified and advance your career."
        keywords="Microsoft training, Dynamics 365 training, Power Platform training, Azure training, live training, virtual training, Microsoft certification, professional development"
        canonical="https://crmonce.com/training"
        ogImage="/images/training-og.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          "name": "CRMONCE Professional Training",
          "description": "Professional Microsoft technology training programs including Dynamics 365, Power Platform, and Azure certifications.",
          "provider": {
            "@type": "Organization",
            "name": "CRMONCE",
            "url": "https://crmonce.com",
            "logo": "https://crmonce.com/logo.png",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-8096556344",
              "contactType": "sales",
              "email": "info@crmonce.com"
            }
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Training Programs",
            "itemListElement": [
              {
                "@type": "Course",
                "name": "Dynamics 365 Fundamentals",
                "description": "Comprehensive training on Dynamics 365 Sales and Customer Service",
                "provider": {
                  "@type": "Organization",
                  "name": "CRMONCE"
                }
              },
              {
                "@type": "Course",
                "name": "Power Platform Development",
                "description": "Learn to build applications with Power Apps, Power BI, and Power Automate",
                "provider": {
                  "@type": "Organization",
                  "name": "CRMONCE"
                }
              }
            ]
          }
        }}
      />

      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
        {/* Hero Section */}
        <section className="pt-48 pb-20 bg-gradient-to-r from-green-600 to-blue-600 text-white">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="flex items-center justify-center mb-6">
                <GraduationCap className="w-12 h-12 mr-4" />
                <h1 className="text-5xl font-bold">Professional Training Programs</h1>
              </div>
              <p className="text-xl text-green-100 mb-8">
                Empower your team with expert-led Microsoft technology training. Live virtual sessions, 
                self-paced online courses, and on-site training programs designed for real-world success.
              </p>
              <motion.button
                onClick={scrollToTrainingPrograms}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-green-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-green-50 transition-all duration-300 cursor-pointer"
              >
                Explore Training Programs
                <ArrowRight className="ml-2 w-5 h-5 inline" />
              </motion.button>
            </motion.div>
          </div>
        </section>

        {/* Training Formats */}
        <section className="py-20">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Flexible Training Formats
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Choose the training format that best fits your schedule, learning style, 
                and business requirements.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {trainingFormats.map((format, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-green-600 mb-4">{format.icon}</div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    {format.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {format.description}
                  </p>
                  <div className="space-y-2">
                    {format.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Training Programs */}
        <section id="training-programs" className="py-20 bg-gray-50">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Training Programs
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive training programs covering all aspects of Microsoft technologies 
                and business process optimization.
              </p>
            </motion.div>

            {/* Format Filter */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {[
                { id: 'all', label: 'All Formats', icon: Globe },
                { id: 'live', label: 'Live Virtual', icon: Video },
                { id: 'online', label: 'Self-Paced', icon: Monitor },
                { id: 'onsite', label: 'On-Site', icon: Users }
              ].map((filter) => (
                <motion.button
                  key={filter.id}
                  onClick={() => setActiveFormat(filter.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                    activeFormat === filter.id
                      ? 'bg-green-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-gray-50 shadow border'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <filter.icon className="w-4 h-4" />
                  <span>{filter.label}</span>
                </motion.button>
              ))}
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPrograms.map((program, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-blue-600 mb-4">{program.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{program.title}</h3>
                  <p className="text-gray-600 mb-4">{program.description}</p>
                  
                  <div className="flex items-center space-x-4 mb-4 text-sm text-gray-500">
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>{program.duration}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Target className="w-4 h-4" />
                      <span>{program.level}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {program.format.map((fmt, fmtIndex) => (
                      <span key={fmtIndex} className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        {fmt === 'live' ? 'Live Virtual' : fmt === 'online' ? 'Self-Paced' : 'On-Site'}
                      </span>
                    ))}
                  </div>

                  <div className="space-y-2">
                    {program.modules.slice(0, 3).map((module, moduleIndex) => (
                      <div key={moduleIndex} className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{module}</span>
                      </div>
                    ))}
                    {program.modules.length > 3 && (
                      <div className="text-gray-500 text-sm">
                        +{program.modules.length - 3} more modules
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Learning Path */}
        <section className="py-20">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Your Learning Journey
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We guide you through a structured learning path that ensures maximum 
                knowledge retention and practical application.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {learningPaths.map((path, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 relative"
                >
                  <div className={`absolute -top-4 -left-4 w-12 h-12 ${path.color} rounded-full flex items-center justify-center text-white font-bold text-lg`}>
                    {path.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">{path.title}</h3>
                  <p className="text-gray-600">{path.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section className="py-20 bg-gray-50">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Microsoft Certifications
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Prepare for official Microsoft certifications that validate your expertise 
                and advance your career in technology.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {certifications.map((cert, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="text-yellow-500">
                      {cert.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{cert.title}</h3>
                  </div>
                  <p className="text-gray-600">{cert.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-r from-green-600 to-blue-600">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Why Choose Our Training?
              </h2>
              <p className="text-xl text-green-100 max-w-3xl mx-auto">
                Experience world-class training with certified instructors and proven methodologies.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center bg-white bg-opacity-10 rounded-lg p-6"
                >
                  <CheckCircle className="w-6 h-6 text-green-400 mr-4 flex-shrink-0" />
                  <span className="text-white font-medium">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-gray-900 to-green-900">
          <div className="container-custom text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Ready to Advance Your Skills?
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Join thousands of professionals who have advanced their careers with our 
                expert-led Microsoft technology training programs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
                >
                  Enroll Now
                  <ArrowRight className="w-5 h-5 ml-2 inline" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors"
                >
                  Download Catalog
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Training;
